import { useState, useEffect, useRef, useCallback } from 'react';
import { checkConnection } from '../api/api';

// Globalny logger do debugowania (dostępny przez window._debugLogs)
declare global {
  interface Window {
    _debugLogs: Array<string>;
  }
}

if (typeof window !== 'undefined' && !window._debugLogs) {
  window._debugLogs = [];
}

const debugLog = (message: string, ...args: Array<unknown>): void => {
  return; //DEBUG MODE OFF
  const logMessage = `[${new Date().toISOString()}] ${message} ${args.map(a => JSON.stringify(a)).join(' ')}`;
  console.log(message, ...args);
  if (typeof window !== 'undefined') {
    window._debugLogs = [...(window._debugLogs || []).slice(-50), logMessage];
  }
};

export interface OnlineStatusConfig {
  // Jak długo czekać przed oznaczeniem jako offline (w ms)
  offlineDelay?: number;
  // Jak długo czekać przed oznaczeniem jako online (w ms)
  onlineDelay?: number;
  // Czy wykonać dodatkowe sprawdzenie połączenia
  verifyConnection?: boolean;
}

export interface OnlineStatus {
  isOnline: boolean;
  connectionQuality: 'good' | 'poor' | 'offline';
}

const DEFAULT_CONFIG: Required<OnlineStatusConfig> = {
  offlineDelay: 3000, // 3 sekundy - daje czas na chwilowe przerwy
  onlineDelay: 1000,  // 1 sekunda - szybciej reaguj na powrót
  verifyConnection: true,
};

/**
 * Custom hook to track online status with debouncing and connection verification
 * @param config Configuration for the hook behavior
 * @returns Object with isOnline status and connection quality info
 */
const useOnlineStatus = (config: OnlineStatusConfig = {}): OnlineStatus => {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [connectionQuality, setConnectionQuality] = useState<'good' | 'poor' | 'offline'>(
    navigator.onLine ? 'good' : 'offline'
  );
  const offlineTimeoutRef = useRef<NodeJS.Timeout>();
  const onlineTimeoutRef = useRef<NodeJS.Timeout>();
  const verificationInProgressRef = useRef(false);
  const initialVerificationDone = useRef(false);
  const intervalIdRef = useRef<NodeJS.Timeout>();

  // Funkcja do weryfikacji rzeczywistego połączenia
  const verifyConnection = useCallback(async (): Promise<boolean> => {
    debugLog('[verifyConnection] Called, verifyConnection enabled:', finalConfig.verifyConnection, 'in progress:', verificationInProgressRef.current);
    
    if (!finalConfig.verifyConnection) {
      debugLog('[verifyConnection] Verification disabled, returning navigator.onLine:', navigator.onLine);
      return navigator.onLine;
    }

    // Jeśli weryfikacja już trwa, poczekaj na jej wynik
    if (verificationInProgressRef.current) {
      debugLog('[verifyConnection] Verification already in progress, skipping duplicate call');
      return navigator.onLine;
    }

    verificationInProgressRef.current = true;
    debugLog('[verifyConnection] Starting verification...');
    
    try {
      // Użyj endpointu /hello z push service
      const isConnected = await checkConnection();
      
      debugLog('[verifyConnection] Result:', isConnected);
      
      return isConnected;
    } catch (error) {
      debugLog('[verifyConnection] Exception:', error);
      return false;
    } finally {
      // Upewnij się że flaga zostanie zresetowana zawsze
      // eslint-disable-next-line require-atomic-updates
      verificationInProgressRef.current = false;
      debugLog('[verifyConnection] Verification completed, flag reset');
    }
  }, [finalConfig.verifyConnection]);

  useEffect(() => {
    debugLog('[useOnlineStatus] useEffect mounted, navigator.onLine:', navigator.onLine);
    
    // Weryfikacja przy pierwszym uruchomieniu
    if (finalConfig.verifyConnection && !initialVerificationDone.current && navigator.onLine) {
      debugLog('[useOnlineStatus] Running initial verification on mount');
      initialVerificationDone.current = true;
      
      // Dodaj timeout jako zabezpieczenie
      const verificationTimeout = setTimeout(() => {
        debugLog('[useOnlineStatus] Initial verification TIMEOUT after 10s');
        setIsOnline(false);
        setConnectionQuality('offline');
      }, 10000); // 10 sekund timeout
      
      verifyConnection().then((isActuallyOnline) => {
        clearTimeout(verificationTimeout);
        debugLog('[useOnlineStatus] Initial verification result:', isActuallyOnline);
        if (!isActuallyOnline) {
          debugLog('[useOnlineStatus] Initial check: Setting offline');
          setIsOnline(false);
          setConnectionQuality('offline');
        } else {
          debugLog('[useOnlineStatus] Initial check: Connection is good');
          setConnectionQuality('good');
        }
      }).catch((error) => {
        clearTimeout(verificationTimeout);
        debugLog('[useOnlineStatus] Initial verification error:', error);
        setIsOnline(false);
        setConnectionQuality('offline');
      });
    }

    const handleOnline = (): void => {
      debugLog('[useOnlineStatus] handleOnline called');
      
      // Wyczyść timeout offline jeśli istnieje
      if (offlineTimeoutRef.current) {
        clearTimeout(offlineTimeoutRef.current);
      }

      // Opcjonalnie zweryfikuj połączenie przed oznaczeniem jako online
      if (finalConfig.verifyConnection) {
        verifyConnection().then((isActuallyOnline) => {
          if (!isActuallyOnline) {
            setConnectionQuality('poor');
            return;
          }
          
          // Dodaj opóźnienie przed oznaczeniem jako online
          onlineTimeoutRef.current = setTimeout(() => {
            setIsOnline(true);
            setConnectionQuality('good');
          }, finalConfig.onlineDelay);
        }).catch(() => {
          setConnectionQuality('poor');
        });
      } else {
        // Dodaj opóźnienie przed oznaczeniem jako online
        onlineTimeoutRef.current = setTimeout(() => {
          setIsOnline(true);
          setConnectionQuality('good');
        }, finalConfig.onlineDelay);
      }
    };

    const handleOffline = (): void => {
      debugLog('[useOnlineStatus] handleOffline called');
      
      // Wyczyść timeout online jeśli istnieje
      if (onlineTimeoutRef.current) {
        clearTimeout(onlineTimeoutRef.current);
      }

      // Wyczyść poprzedni timeout offline jeśli istnieje
      if (offlineTimeoutRef.current) {
        clearTimeout(offlineTimeoutRef.current);
      }

      // Oznacz słabe połączenie natychmiast
      setConnectionQuality('poor');

      // Zweryfikuj połączenie przed oznaczeniem jako offline
      if (finalConfig.verifyConnection) {
        debugLog('[useOnlineStatus] Starting connection verification');
        verifyConnection().then((isActuallyOnline) => {
          debugLog('[useOnlineStatus] Verification result:', isActuallyOnline);
          if (!isActuallyOnline) {
            // Rzeczywiście offline - ustaw natychmiast (weryfikacja już zajęła czas)
            debugLog('[useOnlineStatus] Setting offline status NOW (verification confirmed offline)');
            setIsOnline(false);
            setConnectionQuality('offline');
          } else {
            // False alarm - wróć do good
            debugLog('[useOnlineStatus] False alarm, connection is good');
            setConnectionQuality('good');
          }
        }).catch((error) => {
          // Błąd weryfikacji = offline
          debugLog('[useOnlineStatus] Verification error:', error);
          setIsOnline(false);
          setConnectionQuality('offline');
        });
      } else {
        // Bez weryfikacji - po prostu ustaw timeout
        offlineTimeoutRef.current = setTimeout(() => {
          setIsOnline(false);
          setConnectionQuality('offline');
        }, finalConfig.offlineDelay);
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return (): void => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (offlineTimeoutRef.current) clearTimeout(offlineTimeoutRef.current);
      if (onlineTimeoutRef.current) clearTimeout(onlineTimeoutRef.current);
      // Note: intervalIdRef cleanup is handled in separate useEffect
    };
  }, [finalConfig.offlineDelay, finalConfig.onlineDelay, finalConfig.verifyConnection, isOnline, connectionQuality, verifyConnection]);

  // Separate effect to reconfigure interval when online status changes
  // This ensures we use faster interval (5s) when offline, slower (30s) when online
  useEffect(() => {
    debugLog('[useOnlineStatus] isOnline changed to:', isOnline, '- reconfiguring interval');
    
    // Clear existing interval
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
    }
    
    // Setup new interval with appropriate timing
    const checkInterval = isOnline ? 30000 : 5000;
    debugLog('[useOnlineStatus] New interval:', checkInterval, 'ms');
    
    intervalIdRef.current = setInterval(() => {
      if (finalConfig.verifyConnection && !verificationInProgressRef.current) {
        debugLog('[useOnlineStatus] Periodic verification, current state:', {
          isOnline,
          navigatorOnline: navigator.onLine,
          quality: connectionQuality
        });
        
        verifyConnection().then((isActuallyOnline) => {
          // Case 1: Was online, now offline
          if (!isActuallyOnline && isOnline) {
            debugLog('[useOnlineStatus] Periodic check: Connection lost');
            setIsOnline(false);
            setConnectionQuality('offline');
          } 
          // Case 2: Was offline, now online ← KEY FIX for iOS!
          else if (isActuallyOnline && !isOnline) {
            debugLog('[useOnlineStatus] Periodic check: Connection restored from offline!');
            setIsOnline(true);
            setConnectionQuality('good');
          }
          // Case 3: Online but poor quality, now good
          else if (isActuallyOnline && connectionQuality === 'poor') {
            debugLog('[useOnlineStatus] Periodic check: Connection quality improved');
            setIsOnline(true);
            setConnectionQuality('good');
          }
          // Case 4: Still offline (no change)
          else if (!isActuallyOnline && !isOnline) {
            debugLog('[useOnlineStatus] Periodic check: Still offline');
            // No change needed
          }
        }).catch((error) => {
          debugLog('[useOnlineStatus] Periodic verification error:', error);
          // Don't change state on verification error
        });
      }
    }, checkInterval);

    // Cleanup on unmount or when isOnline changes
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
    };
  }, [isOnline, connectionQuality, finalConfig.verifyConnection, verifyConnection]);

  return { isOnline, connectionQuality };
};

export default useOnlineStatus; 